import React, { useMemo } from 'react';
import Link from 'next/link';

import { Table, TableBody, TableCell, TableHead, TableHeadCell, TableHeadRow } from './table';
import { IStandings } from '../models/standings';
import { displayClubNameWithMark, yearMappings } from '@/utils/showClubNamePeriodicTitle';

interface IProps {
	clubs: IStandings[];
	legend?: boolean;
	snug?: boolean;
	invertedHeaderColors?: boolean;
	className?: string;
	displayPeriodTitle?: boolean;
	isHistoric?: boolean;
	showCorornaLegend?: boolean;
	year: string; // made required
	startIndex?: number;
}

export type PromotionTeamsType = { [key: string]: boolean | undefined };

const MAX_PROMOTION_TEAMS = 8;

const getLegendClassName = (club: IStandings, promotionTeams: PromotionTeamsType) => {
	switch (promotionTeams[club.contestantId]) {
		case true:
			return 'legend-blue';
		case false:
			return 'legend-yellow';
		default:
			return undefined;
	}
};

function getClassNameForRankStatus(rankStatus: string | undefined): string {
	switch (rankStatus) {
		case 'green':
			return 'legend-blue';
		case 'red':
			return 'legend-yellow';
		default:
			return '';
	}
}

const resultSplitter = (lastSix: string) => {
	const translationMap: { [key: string]: string } = {
		W: 'W',
		D: 'G',
		L: 'V',
	};
	const lettersArray = lastSix?.split('');
	const translatedLettersArray = lettersArray?.map((letter) => {
		return translationMap[letter] || letter;
	});

	return (
		<div>
			{translatedLettersArray?.map((letter, i) => (
				<span key={i} className={`result-${letter}`}>
					{letter}
				</span>
			))}
		</div>
	);
};

export default function StandingsTable({
	clubs,
	legend,
	snug,
	invertedHeaderColors,
	className,
	displayPeriodTitle,
	isHistoric,
	showCorornaLegend,
	year,
	startIndex = 0,
}: IProps) {
	const promotionTeams = useMemo(() => {
		// Filter out Jong teams that cannot promote
		let teams = clubs.filter((club) => !club.contestantName.toLowerCase().includes('jong'));

		const maxPromotionCandidates = !isHistoric ? MAX_PROMOTION_TEAMS : 9;
		let promotionCandidates = teams.slice(0, maxPromotionCandidates);

		// Get period champions based on the current year
		const currentYearMapping = yearMappings[year || '2026/2027'] || {};
		const periodChampionCodes = Object.keys(currentYearMapping);

		// Add period champions to promotion candidates if they're not already included
		periodChampionCodes.forEach((championCode) => {
			const champion = teams.find((club) => club.contestantCode === championCode);
			if (champion && !promotionCandidates.find((club) => club.contestantId === champion.contestantId)) {
				promotionCandidates.pop();
				promotionCandidates.push(champion);
			}
		});

		promotionCandidates.sort((a, b) => {
			const indexA = teams.findIndex((club) => club.contestantId === a.contestantId);
			const indexB = teams.findIndex((club) => club.contestantId === b.contestantId);
			return indexA - indexB;
		});

		return promotionCandidates.reduce((acc, club, i) => {
			acc[club.contestantId] = i < (isHistoric ? 1 : 2) ? true : false;
			return acc;
		}, {} as PromotionTeamsType);
	}, [clubs, year, isHistoric]);

	return (
		<div className="overflow-x-auto">
			<Table className={`table-auto overflow-scroll w-full ${className || ''}`} snug={snug}>
				<TableHead>
					<TableHeadRow className={invertedHeaderColors ? 'text-white' : 'text-black-500'}>
						<TableHeadCell className="w-1">
							<span className="sr-only">Legenda</span>
						</TableHeadCell>
						<TableHeadCell className="text-center w-3">#</TableHeadCell>
						<TableHeadCell
							className={
								snug
									? 'font-bold hidden md:table-cell lg:table-cell min-[1280px]:hidden min-[1536px]:table-cell'
									: 'font-bold hidden lg:table-cell'
							}
						></TableHeadCell>
						<TableHeadCell className="text-left">Club</TableHeadCell>
						<TableHeadCell>GS</TableHeadCell>
						<TableHeadCell className="w-[80px] md:w-auto">W / G / V</TableHeadCell>
						<TableHeadCell>P</TableHeadCell>
						<TableHeadCell className={snug ? 'inline-block w-[70px]' : ''}>DV / DT</TableHeadCell>
						<TableHeadCell>DS</TableHeadCell>
						<TableHeadCell className="hidden md:table-cell">Vorm</TableHeadCell>
					</TableHeadRow>
				</TableHead>
				<TableBody className="bg-white">
					{clubs.map((club, i) => (
						<tr key={club.contestantId} className="even:bg-gray-50">
							<>
								{legend ? (
									<TableCell className="w-2">
										<div className={getLegendClassName(club, promotionTeams)} />
									</TableCell>
								) : (
									<TableCell className="w-2">
										<div className={getClassNameForRankStatus(club.rankStatus)} />
									</TableCell>
								)}
							</>
							<TableCell className="text-center w-3">{startIndex + (i + 1)}</TableCell>
							<TableCell nowrap className="text-center min-w-[2.75rem]">
								{club.contestantLogo && (
									<Link href={`/teams/${club.contestantClubSlug}`}>
										<img
											src={club.contestantLogo}
											alt={club.contestantClubName}
											className={
												snug
													? 'inline-block w-6 h-6'
													: 'inline-block sm:w-10 sm:h-10 aspect-square object-contain'
											}
										/>
									</Link>
								)}
							</TableCell>
							<TableCell
								nowrap
								className={
									snug
										? 'font-bold hidden md:table-cell lg:table-cell min-[1280px]:hidden min-[1536px]:table-cell'
										: 'font-bold hidden lg:table-cell'
								}
							>
								<Link href={`/teams/${club.contestantClubSlug}`}>
									{displayClubNameWithMark(club, year) || club.contestantClubName}
								</Link>
							</TableCell>
							<TableCell nowrap>{club.matchesPlayed}</TableCell>
							<TableCell nowrap>
								{club.matchesWon} / {club.matchesDrawn} / {club.matchesLost}
							</TableCell>
							<TableCell nowrap>{club.points}</TableCell>
							<TableCell nowrap>
								{club.goalsFor} / {club.goalsAgainst}
							</TableCell>
							<TableCell nowrap>{club.goalDifference}</TableCell>
							<TableCell className="hidden md:table-cell" nowrap>
								{resultSplitter(club.lastSix)}
							</TableCell>
						</tr>
					))}
				</TableBody>
			</Table>

			{legend && (
				<div className="block md:flex flex-row justify-between my-8 ">
					<div className="flex flex-wrap flex-row items-start md:flex-nowrap gap-y-3">
						<div className="flex flex-row justify-center items-center mr-4">
							<div className="legend-blue-lg mr-2" />
							<span className={invertedHeaderColors ? 'text-white' : 'text-black-500'}>
								Promotie Eredivisie
							</span>
						</div>
						<div className="flex flex-row justify-center items-center mr-4">
							<div className="legend-yellow-lg mr-2" />
							<span className={invertedHeaderColors ? 'text-white' : 'text-black-500'}>
								Keuken Kampioen Play-Offs
							</span>
						</div>
						{displayPeriodTitle && (
							<div className="flex-none basis-full md:flex md:flex-1 ml-3 flex-row justify-center items-center leading-[32px] md:ml-4">
								<p className={invertedHeaderColors ? 'text-white ' : 'text-black-500'}>
									+ Periodekampioen
								</p>
							</div>
						)}
					</div>
				</div>
			)}

			{showCorornaLegend && (
				<div className="block md:flex flex-row justify-between my-8 ">
					<div className="my-6 mx-3 md:m-1 flex items-center">
						* Door het annuleren van de competitie in verband met Covid werd er geen kampioen aangewezen en
						werden er geen play-offs gespeeld.
					</div>
				</div>
			)}
		</div>
	);
}
